import { findIndex } from 'lodash';
import moment from 'moment';

import { wonGame } from '.';
import { OutComeTypes, CompletionTypes, MIN_GAME_DIFF_FOR_NON_TIED_SET, DisplayStates, WalkOverValues, RetiredValues, RedirectUrlPages } from './postScoreConstants';
import { drawPositionTypeIds } from './eventConstants';
import { participatingMemberToPlayerObject } from './participatingMemberUtils';
import links from '../../config/links';
import { isSinglesFormat, isTennisSportTypeEvent } from './events';

const _ = { findIndex };

// Normalizes match positions for post-score for draws and schedule tab : when typeId is PLAYER and position has memberIds
export const normalizeMatchPositionsForPostScore = (match) => {
    const pos1 = match?.position1;
    const pos2 = match?.position2;
    const position1 = pos1?.typeId === drawPositionTypeIds.PLAYER && (pos1.memberIds?.length > 0)
        ? { ...pos1, memberId: pos1.memberIds[0] }
        : pos1;
    const position2 = pos2?.typeId === drawPositionTypeIds.PLAYER && (pos2.memberIds?.length > 0)
        ? { ...pos2, memberId: pos2.memberIds[0] }
        : pos2;
    return position1 !== pos1 || position2 !== pos2 ? { ...match, position1, position2 } : match;
};

export const pos1Won = (set) => {
    // eslint-disable-next-line no-bitwise
    if (set.pos1Score && set.pos2Score && (set.pos1Score > ~~set.pos2Score)) {
        return true;
    }

    // eslint-disable-next-line no-bitwise
    if (set.pos1TieBrk && set.pos2TieBrk && (set.pos1TieBrk > ~~set.pos2TieBrk + 1) && (set.pos1Score > ~~set.pos2Score)) {
        return true;
    }
    return false;
};

export const pos2Won = (set) => {
    // eslint-disable-next-line no-bitwise
    if (set.pos1Score && set.pos2Score && (set.pos2Score > ~~set.pos1Score)) {
        return true;
    }

    // eslint-disable-next-line no-bitwise
    if (set.pos1TieBrk && set.pos2TieBrk && (set.pos2TieBrk > ~~set.pos1TieBrk + 1) && (set.pos2Score > ~~set.pos1Score)) {
        return true;
    }
    return false;
};

export const tieBreakScoreExists = (set) => {
    return (set.pos1TieBrk !== '' || set.pos2TieBrk !== '');
};

export const tie = (set, sportTypeId) => {
    if (set.pos1Score && set.pos2Score && (Math.abs(parseInt(set.pos1Score) - parseInt(set.pos2Score)) < MIN_GAME_DIFF_FOR_NON_TIED_SET)) {
        return !(set.pos1Score === set.pos2Score && isTennisSportTypeEvent(sportTypeId));
    }
    return false;
};

const getPositionLabel = ({ isSide1, isPrimary, winningTeam, matchOutCome, isReverseView, isDoubles }) => {
    const myTeam = isSide1 ? 'team1' : 'team2';
    const myNum = isSide1 ? 1 : 2;
    const oppNum = isSide1 ? 2 : 1;
    const displayNum = isReverseView ? oppNum : myNum;
    const teamLabel = `Team ${displayNum}`;
    const playerLabel = `Player ${displayNum}`;

    if (winningTeam === myTeam) {
        return isDoubles ? `${teamLabel} - Winner` : `${playerLabel} - Winner`;
    }
    if (matchOutCome) {
        if (isDoubles) {
            return `${teamLabel} - ${matchOutCome}`;
        }
        return isPrimary ? `${playerLabel} - ${matchOutCome}` : matchOutCome;
    }
    if (isDoubles) {
        return teamLabel;
    }
    return isPrimary ? playerLabel : '';
};

export const getPlayerNumber = ({ position, winningTeam, matchOutCome, isReverseView, isDoubles }) => {
    switch (position) {
        case 'pos1player1':
            return getPositionLabel({ isSide1: true, isPrimary: true, winningTeam, matchOutCome, isReverseView, isDoubles });
        case 'pos1player2':
            return getPositionLabel({ isSide1: true, isPrimary: false, winningTeam, matchOutCome, isReverseView, isDoubles });
        case 'pos2player1':
            return getPositionLabel({ isSide1: false, isPrimary: true, winningTeam, matchOutCome, isReverseView, isDoubles });
        case 'pos2player2':
            return getPositionLabel({ isSide1: false, isPrimary: false, winningTeam, matchOutCome, isReverseView, isDoubles });
        default:
            return '';
    }
};

export const checkIfPlayerWon = ({ position, isWinner, winningTeam }) => {
    if (isWinner && winningTeam) {
        if (winningTeam === 'team1' && (position === 'pos1player1' || position === 'pos1player2')) {
            return true;
        }
        if (winningTeam === 'team2' && (position === 'pos2player1' || position === 'pos2player2')) {
            return true;
        }
        return false;
    }
    return false;
};

export const position1winner = (match, teamsDict, playersDictByMemberId) => {
    // V3 path: use participatingMembers (Court Scheduler context)
    if (match.participatingMembers) {
        const memberIds = match.position1?.memberIds ?? (match.position1?.memberId ? [match.position1.memberId] : []);
        const playerIds = memberIds.map((memberId) => {
            const member = match.participatingMembers.find((m) => m.id === memberId);
            return member?.playerId;
        }).filter(Boolean);

        const winner1 = match.result?.players?.winner1;
        const winner2 = match.result?.players?.winner2;

        return playerIds.some((pid) => pid === winner1 || pid === winner2);
    }

    // v1 singles
    if (match.position1?.id && match.position1.type?.value === 'player') {
        return wonGame(match.result?.players, match.position1.id);
    }
    // v2 singles
    if (match.position1?.memberId && match.position1.typeId === drawPositionTypeIds.PLAYER) {
        return wonGame(match.result?.players, playersDictByMemberId[match.position1.memberId]?.playerId);
    }
    // v1 doubles
    if (match.position1?.id && match.position1.type?.value === 'team') {
        return wonGame(match.result?.players, teamsDict[match.position1.id]?.players?.[0]);
    }
    // v2 doubles
    if (match.position1?.teamId && match.position1.typeId === drawPositionTypeIds.TEAM) {
        return wonGame(match.result?.players, playersDictByMemberId[teamsDict[match.position1.teamId]?.members?.[0]]?.playerId);
    }
    return false;
};

const getScoreIncludingZero = (score) => {
    if (score || score === 0) {
        return score.toString();
    }

    return '';
};

export const resultToScore = (match, teamsDict, playersDictByMemberId) => {
    const matchSets = [];
    if (position1winner(match, teamsDict, playersDictByMemberId)) {
        for (let i = 0; i < 3; i += 1) {
            matchSets.push({
                id: i,
                pos1Score: getScoreIncludingZero(match.result?.score[i + 1]?.winner),
                pos2Score: getScoreIncludingZero(match.result?.score[i + 1]?.loser),
                pos1TieBrk: getScoreIncludingZero(match.result?.score[i + 1]?.winnerTiebreak),
                pos2TieBrk: getScoreIncludingZero(match.result?.score[i + 1]?.tiebreak),
            });
        }
    } else {
        for (let i = 0; i < 3; i += 1) {
            matchSets.push({
                id: i,
                pos1Score: getScoreIncludingZero(match.result?.score[i + 1]?.loser),
                pos2Score: getScoreIncludingZero(match.result?.score[i + 1]?.winner),
                pos1TieBrk: getScoreIncludingZero(match.result?.score[i + 1]?.tiebreak),
                pos2TieBrk: getScoreIncludingZero(match.result?.score[i + 1]?.winnerTiebreak),
            });
        }
    }

    return matchSets;
};

const searchToPlayer = (searchPerson, isEvent, isEdit, playerMatchPost, user) => {
    const player = {};
    if (searchPerson.Id || searchPerson.id) {
        player.id = searchPerson.Id || searchPerson.id;
        if (!(searchPerson.claimed || isEvent || isEdit || playerMatchPost || searchPerson.id === user.claimedPlayer.playerId)) {
            player.invite = true;
            player.firstName = searchPerson.firstName;
            player.lastName = searchPerson.lastName;
        }
    } else if (searchPerson.playerId) { // v2
        player.id = searchPerson.playerId;
    } else {
        player.firstName = searchPerson.firstName || searchPerson.NamePart1 || undefined;
        player.lastName = searchPerson.lastName || searchPerson.NamePart3 || undefined;
        player.invite = true;
    }
    return player;
};

export const getPostScoreData = (data) => {
    const {
        match,
        sets,
        date,
        isEvent,
        isEdit,
        isDoubles,
        isWinner,
        isCompleted,
        matchOutCome,
        winningTeam,
        playerMatchPost,
        user,
        isVerifiedResult,
        excludeFromRating,
    } = data;

    const postScoreData = {
        date,
        teamType: 'S',
        isWinner: false,
    };
    if (isDoubles) {
        postScoreData.teamType = 'D';
    }

    if (match?.id) {
        postScoreData.matchId = match.id;
    }
    if (match?.result) {
        postScoreData.resultId = match.result.id;
    }

    postScoreData.winner1 = winningTeam === 'team2' ? searchToPlayer(match.pos2player1, isEvent, isEdit, playerMatchPost, user) : searchToPlayer(match.pos1player1, isEvent, isEdit, playerMatchPost, user);
    postScoreData.loser1 = winningTeam === 'team2' ? searchToPlayer(match.pos1player1, isEvent, isEdit, playerMatchPost, user) : searchToPlayer(match.pos2player1, isEvent, isEdit, playerMatchPost, user);

    if (isDoubles) {
        postScoreData.winner2 = winningTeam === 'team2' ? searchToPlayer(match.pos2player2, isEvent, isEdit, playerMatchPost, user) : searchToPlayer(match.pos1player2, isEvent, isEdit, playerMatchPost, user);
        postScoreData.loser2 = winningTeam === 'team2' ? searchToPlayer(match.pos1player2, isEvent, isEdit, playerMatchPost, user) : searchToPlayer(match.pos2player2, isEvent, isEdit, playerMatchPost, user);
    }

    sets.forEach((set, index) => {
        postScoreData[`winnerSet${index + 1}`] = winningTeam === 'team2' ? set.pos2Score : set.pos1Score;
        postScoreData[`loserSet${index + 1}`] = winningTeam === 'team2' ? set.pos1Score : set.pos2Score;
        postScoreData[`tiebreakerSet${index + 1}`] = pos2Won(set) ? set.pos1TieBrk || undefined : set.pos2TieBrk || undefined;
    });

    postScoreData.resultSourceType = isVerifiedResult ? 0 : 1;
    if (!isEvent) {
        postScoreData.excludeFromRating = excludeFromRating;
    }
    postScoreData.isWinner = isWinner;
    postScoreData.completed = isCompleted;

    const validMatchOutComes = [OutComeTypes.DEFAULTED, OutComeTypes.RETIRED, OutComeTypes.WITHDREW, OutComeTypes.FORFEIT, OutComeTypes.ABANDONED, OutComeTypes.DOUBLEWALKOVER];
    if (validMatchOutComes.includes(matchOutCome)) {
        postScoreData.matchOutcome = matchOutCome;
    }

    return postScoreData;
};

const setHasScore = (set) => {
    return set.pos1Score.toString().length > 0 || set.pos2Score.toString().length > 0;
};

const tieBreakHasScore = set => {
    return set.pos1TieBrk.toString().length > 0 || set.pos2TieBrk.toString().length > 0;
};

const validateTieBreakScore = set => {
    // only enforce rules with a tiebreaker score added and if there is a difference in game total
    // ignore if it's 6-6 and any random tiebreak score
    if (set.pos1Score !== set.pos2Score) {
        if (parseInt(set.pos1Score) > parseInt(set.pos2Score)) {
            return parseInt(set.pos1TieBrk) > parseInt(set.pos2TieBrk);
        }
        return parseInt(set.pos2TieBrk) > parseInt(set.pos1TieBrk);
    }
    // return true because allow them to insert any tie-break score
    return true;
};

const validateScores = (sets) => {
    let pos1Sets = 0;
    let pos2Sets = 0;
    let correctOrder = true;
    let matchHasScores = false;
    let validTieBreakScores = true;
    for (let i = 0; i < sets.length; i += 1) {
        if (setHasScore(sets[i])) {
            matchHasScores = true;
            // if second set onwards && if previous set doesn't have a score, set an error
            if (i !== 0 && !setHasScore(sets[i - 1])) {
                correctOrder = false;
            }
            // if either tiebreak input is enabled and has a value, we have to validate
            if (tieBreakHasScore(sets[i])) {
                if (!validateTieBreakScore(sets[i])) {
                    validTieBreakScores = false;
                }
            }
        }
        if (pos1Won(sets[i])) {
            pos1Sets += 1;
        } else if (pos2Won(sets[i])) {
            pos2Sets += 1;
        }
    }

    if (matchHasScores) {
        if (!correctOrder) {
            return 'Please enter set scores in correct order.';
        }
        if (!validTieBreakScores) {
            return 'Please enter valid tiebreak scores';
        }
        if (Math.abs(pos1Sets - pos2Sets) > 2) {
            return 'Match must be 2 out of 3 sets';
        }
    }

    return null;
};

const validatePickleballScores = (sets) => {
    let correctOrder = true;
    let matchHasScores = false;
    for (let i = 0; i < sets.length; i += 1) {
        if (setHasScore(sets[i])) {
            matchHasScores = true;
            // if second set onwards && if previous set doesn't have a score, set an error
            if (i !== 0 && !setHasScore(sets[i - 1])) {
                correctOrder = false;
            }
        }
    }

    if (matchHasScores) {
        if (!correctOrder) {
            return 'Please enter set scores in correct order.';
        }
    }

    return null;
};

const setsWithScore = (sets) => {
    let scoredSets = 0;
    sets.forEach((set) => {
        if (setHasScore(set)) {
            scoredSets += 1;
        }
    });
    return scoredSets;
};

const validateVerifiedMatchScores = (data) => {
    const { sets, isWinner, isCompleted, isVerifiedResult, matchOutCome, isColorball, isTimeBasedDraw } = data;
    const validMatchOutComes = [OutComeTypes.DEFAULTED, OutComeTypes.RETIRED, OutComeTypes.WITHDREW, OutComeTypes.FORFEIT, OutComeTypes.ABANDONED, OutComeTypes.DOUBLEWALKOVER];

    if (isVerifiedResult && (isCompleted && !validMatchOutComes.includes(matchOutCome)) && !isColorball) {
        const setsPlayed = setsWithScore(sets);
        let minSetWinGameScore = 4;
        let minNumberOfSetsPlayed = 2;
        if (setsPlayed === 1) {
            minSetWinGameScore = isTimeBasedDraw ? 4 : 6;
            minNumberOfSetsPlayed = 1;
        }
        let complyingScore = true;
        let anySetHasMinGames = false;
        sets.forEach((set, i) => {
            const hasMinGames = (set.pos1Score.toString().length > 0 && set.pos1Score >= minSetWinGameScore) || (set.pos2Score.toString().length > 0 && set.pos2Score >= minSetWinGameScore);

            if (hasMinGames) {
                anySetHasMinGames = true;
            }

            if (i < minNumberOfSetsPlayed && !isTimeBasedDraw && !hasMinGames) {
                complyingScore = false;
            }
        });

        // If this is a time-based draw, require that at least one set reaches the minimum games
        if (isTimeBasedDraw && !anySetHasMinGames) {
            complyingScore = false;
        }
        if (!isWinner || !complyingScore) {
            return `Score must comply with <a href="${links.verifiedRules}" target="_blank">Verified format</a>`;
        }
    }

    return null;
};

const checkForDuplicatePlayers = (data) => {
    const players = [];
    const playerIds = [];

    players.push(data.winner1);
    players.push(data.loser1);

    if (data.winner2) {
        players.push(data.winner2);
    }

    if (data.loser2) {
        players.push(data.loser2);
    }

    // Check for duplicate players
    let error;
    players.forEach((player) => {
        if (player?.id) {
            const alreadyExists = _.findIndex(playerIds, id => id === player.id) !== -1;
            if (alreadyExists) {
                error = 'Duplicate player in match';
            } else {
                playerIds.push(player.id);
            }
        }
    });

    return error;
};

export const validatePostScoreData = (data) => {
    let error;
    error = validateScores(data.sets);
    if (!error) {
        error = validateVerifiedMatchScores(data);
    }
    if (!error) {
        error = checkForDuplicatePlayers(data.postScoreData);
    }

    return {
        valid: !error,
        error,
    };
};

export const validatePickleballPostScoreData = (data) => {
    let error;
    error = validatePickleballScores(data.sets);
    if (!error) {
        error = checkForDuplicatePlayers(data.postScoreData);
    }

    return {
        valid: !error,
        error,
    };
};

export const checkIfPlayerInResult = (playerId, winner1, loser1, winner2, loser2) => {
    const playerIdList = [];
    playerIdList.push(parseInt(winner1));
    playerIdList.push(parseInt(loser1));
    if (winner2) {
        playerIdList.push(parseInt(winner2));
    }
    if (loser2) {
        playerIdList.push(parseInt(loser2));
    }
    return playerIdList.indexOf(parseInt(playerId)) !== -1;
};

export const showOutcome = (outcome) => {
    return outcome !== OutComeTypes.ABANDONED && outcome !== OutComeTypes.DOUBLEWALKOVER && outcome !== OutComeTypes.DEFAULTED && outcome !== OutComeTypes.WITHDREW && outcome !== OutComeTypes.FORFEIT;
};

export const isPendingMatch = (playerPostedResult, finalized, isRejected, playerId, winner1, loser1, winner2, loser2) => {
    if (playerPostedResult && !finalized && !isRejected) {
        return true;
    }
    return !finalized && checkIfPlayerInResult(playerId, winner1, loser1, winner2, loser2);
};

export const showMatchWinner = (outcome, completionType) => {
    // Winner triangles should not be shown for incomplete and tie matches
    return !(outcome === OutComeTypes.INCOMPLETE || completionType === CompletionTypes.TIE_INCOMPLETE || completionType === CompletionTypes.TIE_COMPLETED || outcome === OutComeTypes.ABANDONED || outcome === OutComeTypes.DOUBLEWALKOVER);
};

const countSetWins = (sets) => {
    let pos1Sets = 0;
    let pos2Sets = 0;
    let hasNoScore = true;

    sets.forEach((set) => {
        if (set.pos1Score.toString().length > 0 || set.pos2Score.toString().length > 0) {
            hasNoScore = false;
        }
        if (pos1Won(set)) {
            pos1Sets += 1;
        } else if (pos2Won(set)) {
            pos2Sets += 1;
        } else {
            // neither player won this set
        }
    });

    return { pos1Sets, pos2Sets, hasNoScore };
};

const baseMatchStatus = (pos1Sets, pos2Sets, hasNoScore) => {
    if (hasNoScore) {
        return { isWinner: false, isCompleted: false, winningTeam: null, displayState: DisplayStates.NO_SCORES };
    }
    if (pos1Sets === pos2Sets) {
        return { isWinner: false, isCompleted: false, winningTeam: null, displayState: DisplayStates.WINNER_NOT_DECIDED };
    }
    return {
        isWinner: true,
        isCompleted: true,
        winningTeam: pos1Sets > pos2Sets ? 'team1' : 'team2',
        displayState: DisplayStates.WINNER_DECIDED,
    };
};

const resolveOutcomeWinner = (matchOutCome, pos1Sets, pos2Sets, match, teamsDict, playersDictByMemberId) => {
    if (WalkOverValues.includes(matchOutCome) || RetiredValues.includes(matchOutCome)) {
        return position1winner(match, teamsDict, playersDictByMemberId) ? 'team1' : 'team2';
    }
    return pos1Sets > pos2Sets ? 'team1' : 'team2';
};

const applyOutcomeOverride = ({ status, matchOutCome, pos1Sets, pos2Sets, hasNoScore, match, teamsDict, playersDictByMemberId }) => {
    if (!matchOutCome) {
        return status;
    }

    const result = { ...status };

    switch (matchOutCome) {
        case OutComeTypes.INCOMPLETE:
            result.winningTeam = null;
            result.displayState = DisplayStates.WINNER_NOT_DECIDED;
            break;
        case OutComeTypes.DOUBLEWALKOVER:
            result.winningTeam = null;
            result.displayState = DisplayStates.NO_SCORES;
            break;
        case OutComeTypes.ABANDONED:
            result.winningTeam = null;
            result.displayState = hasNoScore ? DisplayStates.NO_SCORES : DisplayStates.WINNER_NOT_DECIDED;
            break;
        default:
            result.winningTeam = resolveOutcomeWinner(matchOutCome, pos1Sets, pos2Sets, match, teamsDict, playersDictByMemberId);
            result.isWinner = true;
            result.isCompleted = true;
            result.displayState = DisplayStates.WINNER_DECIDED;
            break;
    }

    return result;
};

export const computeMatchStatus = (sets, matchOutCome, match, teamsDict, playersDictByMemberId) => {
    const { pos1Sets, pos2Sets, hasNoScore } = countSetWins(sets);
    const status = baseMatchStatus(pos1Sets, pos2Sets, hasNoScore);
    const result = applyOutcomeOverride({ status, matchOutCome, pos1Sets, pos2Sets, hasNoScore, match, teamsDict, playersDictByMemberId });
    return { ...result, hasNoScore };
};

export const matchPlayersEqual = (match1, match2) => {
    if (!match1 || !match2 || match1.id !== match2.id) {
        return false;
    }

    const positions = ['pos1player1', 'pos1player2', 'pos2player1', 'pos2player2'];

    // v1: compare by id and query
    const v1HasData = positions.some(pos => match1[pos]?.id != null || match2[pos]?.id != null);
    if (v1HasData) {
        return positions.every(pos => match1[pos]?.id === match2[pos]?.id && match1[pos]?.query === match2[pos]?.query);
    }

    // v2: compare by firstName and memberId (covers invited players with no id/query)
    return positions.every(pos => match1[pos]?.firstName === match2[pos]?.firstName && match1[pos]?.memberId === match2[pos]?.memberId);
};

export const resolveMatchOutcome = (result) => {
    if (result?.completionType === 'tieincomplete') {
        return OutComeTypes.INCOMPLETE;
    }
    if (WalkOverValues.includes(result?.outcome) || RetiredValues.includes(result?.outcome)) {
        return result.outcome;
    }
    return null;
};

export const parseMatchDate = (matchObj, timeZone) => {
    if (!matchObj?.date) {
        return moment();
    }
    if (timeZone) {
        return moment.utc(matchObj.date).tz(timeZone.value);
    }
    return moment.utc(matchObj.date).utc();
};

export const clampScoreValue = (rawValue, setId, key, isTieBreak) => {
    let value = rawValue;
    // Support pro set of 10 for SET-1
    if (setId === 0) {
        if (value.length > 2) {
            value %= 100;
        }
        if (parseInt(value) > 11 && !isTieBreak) {
            value = 11;
        }
    } else if (key === 'pos1Score' || key === 'pos2Score') {
        if (value.length > 1) {
            value %= 10;
        }
    } else if (value.length > 2) {
        value %= 100;
    } else {
        // value is within allowed range for tiebreak input
    }
    return value;
};

export const resolveExcludeFromRating = (isEvent, defaultCountsForRating, currentValue) => {
    if (isEvent) {
        return false;
    }
    if (defaultCountsForRating != null) {
        return !defaultCountsForRating;
    }
    return currentValue;
};

export const resolveIsDoubles = (forceDoubles, isEvent, isEdit, playerMatchPost, matchObj) => {
    if (forceDoubles) {
        return true;
    }
    if (isEvent || isEdit || playerMatchPost) {
        return matchObj?.position1?.type?.value === 'team' || matchObj?.position1?.typeId === drawPositionTypeIds.TEAM;
    }
    return false;
};

export const getRedirectUrl = (showCountsForRating, redirectUrlPage) => {
    if (showCountsForRating) {
        return links.addScoreArticleClubs;
    }
    if (redirectUrlPage === RedirectUrlPages.FLEX_LEAGUE) {
        return links.addScoreArticleFlexLeagues;
    }
    if (redirectUrlPage === RedirectUrlPages.EVENT_DESK_POST) {
        return links.addScoreArticleEvents;
    }
    return links.addScoreArticle;
};

// Resolves the player/team dicts the post-score popup should pass downstream.
// Each dict is gated independently — a populated Redux dict is preferred,
// otherwise we derive from `matches`. This is what makes a singles draw work:
// teamsDict is legitimately empty for singles, but playersDictByMemberId
// is still populated from Redux and must not be discarded.
export const resolvePostScorePopupDicts = ({ playersDictByMemberId, teamsDict, players, matches }) => {
    const hasPlayers = Object.keys(playersDictByMemberId ?? {}).length > 0 &&
        (players?.length ?? 0) > 0;
    const hasTeams = Object.keys(teamsDict ?? {}).length > 0;
    if (hasPlayers && hasTeams) {
        return { playersDictByMemberId, teamsDict, players };
    }
    const fallback = buildPlayerAndTeamDictsFromMatches(matches);
    return {
        playersDictByMemberId: hasPlayers ? playersDictByMemberId : fallback.playersDictByMemberId,
        teamsDict: hasTeams ? teamsDict : fallback.teamsDict,
        players: hasPlayers ? players : fallback.players,
    };
};

export const buildPlayerAndTeamDictsFromMatches = (matches) => {
    const playersDictByMemberId = {};
    const teamsDict = {};

    (matches ?? []).forEach(match => {
        (match.participatingMembers ?? []).forEach(member => {
            if (member?.id != null) {
                playersDictByMemberId[member.id] = participatingMemberToPlayerObject(member);
            }
        });
    });

    (matches ?? []).forEach(match => {
        [match.position1, match.position2].forEach(pos => {
            if (pos?.teamId && pos?.memberIds?.length > 0) {
                teamsDict[pos.teamId] = {
                    id: pos.teamId,
                    members: pos.memberIds,
                    players: pos.memberIds
                        .map(mId => playersDictByMemberId[mId]?.playerId)
                        .filter(Boolean),
                };
            }
        });
    });

    const players = Object.values(playersDictByMemberId);

    return { playersDictByMemberId, teamsDict, players };
};

export const areScoresValid = (sets, matchOutCome = null) => {
    const lowerOutcome = matchOutCome?.toLowerCase();
    // These outcomes never require scores — bypass regardless
    const alwaysBypassOutcomes = [OutComeTypes.WITHDREW, OutComeTypes.DOUBLEWALKOVER];
    // These outcomes don't require scores, but if scores are entered they must be valid
    const conditionalOutcomes = [OutComeTypes.DEFAULTED, OutComeTypes.RETIRED, OutComeTypes.INCOMPLETE, OutComeTypes.COMPLETE, OutComeTypes.COMPLETED, OutComeTypes.FORFEIT, OutComeTypes.ABANDONED];

    if (alwaysBypassOutcomes.includes(lowerOutcome)) {
        return true;
    }

    if (!sets || sets.length === 0) {
        return false;
    }

    const isFilled = (score) => score !== null && score !== undefined && score.toString().trim() !== '';

    const hasCompleteSet = sets.some(
        (set) => isFilled(set.pos1Score) && isFilled(set.pos2Score),
    );

    const isValid = sets.every((set) => {
        const p1Filled = isFilled(set.pos1Score);
        const p2Filled = isFilled(set.pos2Score);

        if (p1Filled || p2Filled) {
            return p1Filled && p2Filled;
        }

        return true;
    });

    if (conditionalOutcomes.includes(lowerOutcome)) {
        const hasAnyScore = sets.some((set) => isFilled(set.pos1Score) || isFilled(set.pos2Score));
        if (!hasAnyScore) {
            return true;
        }
        return hasCompleteSet && isValid;
    }

    return hasCompleteSet && isValid;
};

export const resolvePlayerIdForPosition = (position, match, playersDictByMemberId) => {
    if (match?.participatingMembers) {
        if (position?.typeId === drawPositionTypeIds.TEAM && position?.teamId) {
            return position.teamId;
        }
        const memberIds = position?.memberIds ?? (position?.memberId ? [position.memberId] : []);
        if (memberIds.length > 0) {
            const member = match.participatingMembers.find((m) => m.id === memberIds[0]);
            return member?.playerId;
        }
        return null;
    }
    if (isSinglesFormat(match?.drawFormatTypeId)) {
        return playersDictByMemberId?.[position?.memberId]?.playerId;
    }
    return position?.teamId;
};

// Set 0 only: hold on "1" — user may still be typing "10" or "11".
export const shouldAdvanceFocusAfterScore = ({ value, isTieBreak, setId }) => {
    if (!value || isTieBreak) {
        return false;
    }
    if (setId === 0 && parseInt(value, 10) === 1) {
        return false;
    }
    return true;
};

export const getTieDisplay = (set, currentSet, sportTypeId) => {
    const isTie = tie(set, sportTypeId) || tieBreakScoreExists(set);
    return { isTie, showTie: isTie && currentSet === set?.id };
};

export const buildPostScorePlayersData = ({
    winnerId, loserId, isDoubles, match, teamsDict, playersDictByMemberId,
}) => {
    const data = {};

    if (isDoubles) {
        if (match?.participatingMembers) {
            const positionForTeamId = (teamId) => {
                if (match.position1?.teamId === teamId) { return match.position1; }
                if (match.position2?.teamId === teamId) { return match.position2; }
                return null;
            };
            const memberIdsToPlayerIds = (memberIds = []) => memberIds
                .map((memberId) => match.participatingMembers.find((m) => m.id === memberId)?.playerId)
                .filter(Boolean);

            const winningPlayerIds = memberIdsToPlayerIds(positionForTeamId(winnerId)?.memberIds);
            const losingPlayerIds = memberIdsToPlayerIds(positionForTeamId(loserId)?.memberIds);
            [data.winner1, data.winner2] = winningPlayerIds;
            [data.loser1, data.loser2] = losingPlayerIds;
        } else {
            const winningMemberIds = teamsDict?.[winnerId]?.members || [];
            const losingMemberIds = teamsDict?.[loserId]?.members || [];
            winningMemberIds.forEach((memberId) => {
                if (data.winner1) {
                    data.winner2 = playersDictByMemberId?.[memberId]?.playerId;
                } else {
                    data.winner1 = playersDictByMemberId?.[memberId]?.playerId;
                }
            });
            losingMemberIds.forEach((memberId) => {
                if (data.loser1) {
                    data.loser2 = playersDictByMemberId?.[memberId]?.playerId;
                } else {
                    data.loser1 = playersDictByMemberId?.[memberId]?.playerId;
                }
            });
        }
    } else {
        data.winner1 = Number(winnerId);
        data.loser1 = Number(loserId);
    }

    if (match?.result) {
        data.resultId = match.result.id;
    }
    return data;
};
